import React, { useState, useEffect } from "react";
import { initializeApp } from "firebase/app";
import {
  getFirestore,
  collection,
  addDoc,
  getDocs,
  query,
  where,
  updateDoc,
  doc,
  setDoc,
  onSnapshot,
  deleteDoc,
  arrayUnion,
} from "firebase/firestore";

// --- FIREBASE SETUP ---
const firebaseConfig = {
  apiKey: "AIzaSyDYUPmD_OLjKQXM0QTqbhhx1LqYO8fpVEk",
  authDomain: "lekker-g-scoring.firebaseapp.com",
  projectId: "lekker-g-scoring",
  storageBucket: "lekker-g-scoring.firebasestorage.app",
  messagingSenderId: "256118047154",
  appId: "1:256118047154:web:5defd8c71a6157f43e64ab",
};

const app = initializeApp(firebaseConfig);
const db = getFirestore(app);

// BRAND LOGO
const LOGO_URL = `${process.env.PUBLIC_URL || ""}/Lekker G Logo.jpg`;

function getStrokeIndexTeeLabel(gender) {
  return gender === "Woman" ? "Women's tees" : "Men's tees";
}

function normalizeCourseHoles(courseHoles) {
  return Array.from({ length: 18 }, (_, i) => {
    const hole = courseHoles?.[i] || {};
    return {
      hole: i + 1,
      par: Number(hole.par) || 4,
      mensStrokeIndex:
        Number(hole.mensStrokeIndex ?? hole.strokeIndex) || i + 1,
      womensStrokeIndex:
        Number(hole.womensStrokeIndex ?? hole.strokeIndex) || i + 1,
    };
  });
}

// --- LEADERBOARD CALCULATION ENGINE ---
function buildLeaderboardData(comp, participants, scoresList) {
  const isPointsFormat = 
    comp.format === "Stableford" || 
    comp.format === "BBS" || 
    comp.format === "Fourball (Two Scores to Count)";

  const scoresByPlayer = {};
  const scoresByHole = {};

  participants.forEach((p) => {
    scoresByPlayer[p.id] = {
      totalGross: 0,
      totalPoints: 0,
      totalNet: 0,
      holesPlayed: 0,
    };
  });

  scoresList.forEach((s) => {
    if (!scoresByPlayer[s.playerId])
      scoresByPlayer[s.playerId] = {
        totalGross: 0,
        totalPoints: 0,
        totalNet: 0,
        holesPlayed: 0,
      };
    scoresByPlayer[s.playerId].totalGross += s.gross;
    scoresByPlayer[s.playerId].totalPoints += s.points;
    scoresByPlayer[s.playerId].totalNet += s.net;
    scoresByPlayer[s.playerId].holesPlayed += 1;

    if (!scoresByHole[s.hole]) scoresByHole[s.hole] = {};
    scoresByHole[s.hole][s.playerId] = s;
  });

  let board = [];
  let teamFormat = false;

  if (
    comp.format === "BBS" ||
    comp.format === "Fourball (Two Scores to Count)" ||
    comp.format === "Scramble"
  ) {
    teamFormat = true;
    const teamMap = {};

    participants.forEach((p) => {
      const tName =
        p.teamName && p.teamName !== "Individual"
          ? p.teamName
          : p.fourballName || "Solo Players";
      if (!teamMap[tName])
        teamMap[tName] = {
          name: tName,
          totalGross: 0,
          totalPoints: 0,
          totalNet: 0,
          teamHcp: 0,
          holesPlayed: 0,
          members: [],
          handicaps: [],
        };
      teamMap[tName].members.push(p.name);
      teamMap[tName].handicaps.push(Number(p.handicap) || 0);
    });

    if (comp.format === "Scramble" && comp.scrambleScoringMode === "Net") {
      Object.values(teamMap).forEach((t) => {
        let sortedHcps = [...t.handicaps].sort((a, b) => a - b);
        let teamHcp = 0;
        if (sortedHcps[0] !== undefined) teamHcp += sortedHcps[0] * 0.25;
        if (sortedHcps[1] !== undefined) teamHcp += sortedHcps[1] * 0.2;
        if (sortedHcps[2] !== undefined) teamHcp += sortedHcps[2] * 0.15;
        if (sortedHcps[3] !== undefined) teamHcp += sortedHcps[3] * 0.1;
        t.teamHcp = parseFloat(teamHcp.toFixed(1));
      });
    }

    for (let hole = 1; hole <= 18; hole++) {
      if (!scoresByHole[hole]) continue;
      Object.keys(teamMap).forEach((tName) => {
        const pIds = participants
          .filter((p) => {
            const assignedTeam =
              p.teamName && p.teamName !== "Individual"
                ? p.teamName
                : p.fourballName || "Solo Players";
            return assignedTeam === tName;
          })
          .map((p) => p.id);

        let ptsArr = [];
        let grossArr = [];

        pIds.forEach((id) => {
          if (scoresByHole[hole][id]) {
            ptsArr.push(scoresByHole[hole][id].points);
            grossArr.push(scoresByHole[hole][id].gross);
            teamMap[tName].holesPlayed = Math.max(
              teamMap[tName].holesPlayed,
              scoresByPlayer[id]?.holesPlayed || 0
            );
          }
        });

        ptsArr.sort((a, b) => b - a);
        grossArr.sort((a, b) => a - b);

        if (comp.format === "BBS") {
          if (ptsArr.length > 0) teamMap[tName].totalPoints += ptsArr[0];
          if (grossArr.length > 0) teamMap[tName].totalGross += grossArr[0];
        } else if (comp.format === "Fourball (Two Scores to Count)") {
          if (ptsArr.length > 0)
            teamMap[tName].totalPoints += ptsArr[0] + (ptsArr[1] || 0);
          if (grossArr.length > 0)
            teamMap[tName].totalGross += grossArr[0] + (grossArr[1] || 0);
        } else {
          let sumPts = 0;
          let sumGross = 0;
          pIds.forEach((id) => {
            if (scoresByHole[hole][id]) {
              sumPts += scoresByHole[hole][id].points;
              sumGross += scoresByHole[hole][id].gross;
            }
          });
          teamMap[tName].totalPoints += sumPts;
          teamMap[tName].totalGross += sumGross;
        }
      });
    }

    if (comp.format === "Scramble" && comp.scrambleScoringMode === "Net") {
      Object.values(teamMap).forEach((t) => {
        t.totalNet = t.totalGross > 0 ? t.totalGross - t.teamHcp : 0;
      });
    }

    board = Object.values(teamMap);
    if (isPointsFormat) {
      board.sort((a, b) => b.totalPoints - a.totalPoints);
    } else if (comp.format === "Scramble" && comp.scrambleScoringMode === "Net") {
      board.sort((a, b) => (a.totalNet || 999) - (b.totalNet || 999));
    } else {
      board.sort((a, b) => (a.totalGross || 999) - (b.totalGross || 999));
    }
  } else {
    participants.forEach((p) => {
      board.push({ ...p, ...scoresByPlayer[p.id] });
    });
    if (isPointsFormat) {
      board.sort((a, b) => b.totalPoints - a.totalPoints);
    } else {
      board.sort((a, b) => (a.totalGross || 999) - (b.totalGross || 999));
    }
  }

  return { board, teamFormat, isPointsFormat };
}

// --- MAIN APP COMPONENT ---
export default function App() {
  const [currentUser, setCurrentUser] = useState(null);

  useEffect(() => {
    const savedUser = localStorage.getItem("lekker_golf_user");
    if (savedUser) setCurrentUser(JSON.parse(savedUser));
  }, []);

  const handleLogin = (userDoc) => {
    localStorage.setItem("lekker_golf_user", JSON.stringify(userDoc));
    setCurrentUser(userDoc);
  };

  const handleLogout = () => {
    localStorage.removeItem("lekker_golf_user");
    setCurrentUser(null);
  };

  if (!currentUser) return <LoginRegister onLogin={handleLogin} db={db} />;

  return (
    <div className="min-h-screen bg-gray-100 text-gray-800 font-sans pb-10">
      <header className="bg-green-800 text-white p-3 shadow-md flex justify-between items-center">
        <div className="flex items-center space-x-3">
          <img
            src={LOGO_URL}
            alt="Lekker Golf Scoring Logo"
            className="h-12 w-auto object-contain bg-white px-2 py-1 rounded shadow-sm"
          />
          <h1 className="text-lg font-bold tracking-wide uppercase hidden sm:block">
            Lekker Golfday Scoring
          </h1>
        </div>
        <button
          onClick={handleLogout}
          className="text-xs bg-green-900 px-3 py-1.5 rounded font-semibold hover:bg-green-950"
        >
          Logout
        </button>
      </header>

      <main className="p-4 max-w-lg mx-auto">
        {currentUser.role === "Owner" && (
          <OwnerDashboard user={currentUser} db={db} />
        )}
        {currentUser.role === "Organizer" && (
          <OrganizerDashboard user={currentUser} db={db} />
        )}
        {currentUser.role === "Player" && (
          <PlayerDashboard
            user={currentUser}
            currentUserDoc={currentUser}
            db={db}
          />
        )}
        {currentUser.role === "Both" && (
          <BothDashboard user={currentUser} db={db} />
        )}
      </main>
    </div>
  );
}

// --- LOGIN / REGISTER / RESET SYSTEM ---
function LoginRegister({ onLogin, db }) {
  const [view, setView] = useState("login");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [role, setRole] = useState("Player");
  const [name, setName] = useState("");
  const [handicap, setHandicap] = useState("");
  const [error, setError] = useState("");
  const [successMsg, setSuccessMsg] = useState("");

  const AUTHORIZED_OWNER_EMAIL = "marnus1980@gmail.com";

  const handleAuthSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSuccessMsg("");
    const usersRef = collection(db, "app_users");

    try {
      if (view === "login") {
        const q = query(
          usersRef,
          where("email", "==", email),
          where("password", "==", password)
        );
        const snap = await getDocs(q);
        if (snap.empty) throw new Error("Invalid email or password");
        onLogin({ id: snap.docs[0].id, ...snap.docs[0].data() });
      } else if (view === "register") {
        if (password !== confirmPassword) {
          throw new Error("Passwords do not match. Please try again.");
        }

        if (
          role === "Owner" &&
          email.toLowerCase() !== AUTHORIZED_OWNER_EMAIL.toLowerCase()
        ) {
          throw new Error("Unauthorized: You cannot register as an App Owner.");
        }

        const q = query(usersRef, where("email", "==", email));
        const check = await getDocs(q);
        if (!check.empty) throw new Error("Email already registered");

        const newUser = {
          email,
          password,
          role,
          name,
          handicap: Number(handicap) || 0,
        };
        const docRef = await addDoc(usersRef, newUser);
        onLogin({ id: docRef.id, ...newUser });
      } else if (view === "reset") {
        const q = query(usersRef, where("email", "==", email));
        const snap = await getDocs(q);
        if (snap.empty) throw new Error("Email address not found in system.");

        const userDocRef = doc(db, "app_users", snap.docs[0].id);
        await updateDoc(userDocRef, { password: newPassword });
        setSuccessMsg("Password successfully updated! You can now log in.");
        setTimeout(() => {
          setView("login");
          setNewPassword("");
          setSuccessMsg("");
        }, 2000);
      }
    } catch (err) {
      setError(err.message);
    }
  };

  return (
    <div className="min-h-screen bg-green-50 flex items-center justify-center p-4">
      <div className="bg-white p-6 rounded-xl shadow-xl w-full max-w-sm text-center">
        <img
          src={LOGO_URL}
          alt="Lekker Golfday Scoring Logo"
          className="w-64 h-auto mx-auto mb-4 object-contain drop-shadow-sm"
        />
        <p className="text-xs text-gray-500 mb-5 font-semibold uppercase tracking-wider">
          Professional Tournament Hub
        </p>

        {error && (
          <p className="text-red-500 text-xs mb-3 bg-red-50 p-2 rounded">
            {error}
          </p>
        )}
        {successMsg && (
          <p className="text-green-700 text-xs mb-3 bg-green-50 p-2 rounded font-bold">
            {successMsg}
          </p>
        )}

        <form onSubmit={handleAuthSubmit} className="space-y-3 text-left">
          {view === "register" && (
            <>
              <div>
                <label className="text-xs font-bold text-gray-600">
                  Full Name
                </label>
                <input
                  type="text"
                  className="w-full border p-2 rounded text-sm"
                  required
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                />
              </div>
              <div>
                <label className="text-xs font-bold text-gray-600">
                  Account Role
                </label>
                <select
                  className="w-full border p-2 rounded text-sm bg-white"
                  value={role}
                  onChange={(e) => setRole(e.target.value)}
                >
                  <option value="Player">Player</option>
                  <option value="Organizer">Organizer</option>
                  <option value="Both">Both (Play & Organize)</option>
                  <option value="Owner">App Owner</option>
                </select>
              </div>
              {(role === "Player" || role === "Both") && (
                <div>
                  <label className="text-xs font-bold text-gray-600">
                    Official Handicap
                  </label>
                  <input
                    type="number"
                    className="w-full border p-2 rounded text-sm"
                    required
                    value={handicap}
                    onChange={(e) => setHandicap(e.target.value)}
                  />
                </div>
              )}
            </>
          )}

          <div>
            <label className="text-xs font-bold text-gray-600">
              Email Address
            </label>
            <input
              type="email"
              className="w-full border p-2 rounded text-sm"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
          </div>

          {(view === "login" || view === "register") && (
            <div>
              <label className="text-xs font-bold text-gray-600">
                Password
              </label>
              <div className="relative">
                <input
                  type={showPassword ? "text" : "password"}
                  className="w-full border p-2 pr-10 rounded text-sm"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-2 top-2.5 text-gray-500 hover:text-gray-800 text-xs font-bold"
                >
                  {showPassword ? "Hide" : "Show"}
                </button>
              </div>
            </div>
          )}

          {view === "register" && (
            <div>
              <label className="text-xs font-bold text-gray-600">
                Confirm Password
              </label>
              <div className="relative">
                <input
                  type={showConfirmPassword ? "text" : "password"}
                  className="w-full border p-2 pr-10 rounded text-sm"
                  required
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className="absolute right-2 top-2.5 text-gray-500 hover:text-gray-800 text-xs font-bold"
                >
                  {showConfirmPassword ? "Hide" : "Show"}
                </button>
              </div>
            </div>
          )}

          {view === "reset" && (
            <div>
              <label className="text-xs font-bold text-gray-600">
                New Password
              </label>
              <div className="relative">
                <input
                  type={showPassword ? "text" : "password"}
                  className="w-full border p-2 pr-10 rounded text-sm"
                  required
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-2 top-2.5 text-gray-500 hover:text-gray-800 text-xs font-bold"
                >
                  {showPassword ? "Hide" : "Show"}
                </button>
              </div>
            </div>
          )}

          <button
            type="submit"
            className="w-full bg-green-700 text-white p-2.5 rounded font-bold hover:bg-green-800 shadow mt-2"
          >
            {view === "login" && "Log In"}
            {view === "register" && "Register Account"}
            {view === "reset" && "Reset Password"}
          </button>
        </form>

        <div className="mt-4 space-y-1 text-center text-xs font-semibold text-green-700">
          {view === "login" && (
            <>
              <p
                className="cursor-pointer hover:underline"
                onClick={() => {
                  setView("register");
                  setError("");
                }}
              >
                Need an account? Register here
              </p>
              <p
                className="cursor-pointer text-gray-500 hover:underline pt-1"
                onClick={() => {
                  setView("reset");
                  setError("");
                }}
              >
                Forgot Password?
              </p>
            </>
          )}
          {view === "register" && (
            <p
              className="cursor-pointer hover:underline"
              onClick={() => {
                setView("login");
                setError("");
              }}
            >
              Already have an account? Log In
            </p>
          )}
          {view === "reset" && (
            <p
              className="cursor-pointer hover:underline"
              onClick={() => {
                setView("login");
                setError("");
              }}
            >
              &larr; Back to Log In
            </p>
          )}
        </div>
      </div>
    </div>
  );
}

// --- OWNER DASHBOARD ---
function OwnerDashboard({ db }) {
  const [companyName, setCompanyName] = useState("");
  const [sponsorLimit, setSponsorLimit] = useState(18);
  const [generatedCode, setGeneratedCode] = useState("");
  const [competitions, setCompetitions] = useState([]);
  const [organizers, setOrganizers] = useState({});
  const [participantCounts, setParticipantCounts] = useState({});
  const [editingCompetition, setEditingCompetition] = useState(null);
  const [editData, setEditData] = useState({});
  const [dashboardError, setDashboardError] = useState("");
  const [savingCompetition, setSavingCompetition] = useState(false);

  useEffect(() => {
    const unsubscribeCompetitions = onSnapshot(
      collection(db, "competitions"),
      (snapshot) => {
        const allCompetitions = snapshot.docs
          .map((competitionDoc) => ({
            id: competitionDoc.id,
            ...competitionDoc.data(),
          }))
          .filter(
            (competition) =>
              typeof competition.compCode === "string" &&
              competition.compCode.trim()
          )
          .sort((a, b) => {
            const dateA = a.createdAt?.toMillis?.() || 0;
            const dateB = b.createdAt?.toMillis?.() || 0;
            return dateB - dateA;
          });
        setCompetitions(allCompetitions);
        setDashboardError("");
      },
      (error) => setDashboardError(`Unable to load competitions: ${error.message}`)
    );
    const unsubscribeUsers = onSnapshot(
      collection(db, "app_users"),
      (snapshot) => {
        const usersById = {};
        snapshot.docs.forEach((userDoc) => {
          usersById[userDoc.id] = userDoc.data();
        });
        setOrganizers(usersById);
      },
      (error) => setDashboardError(`Unable to load organizer details: ${error.message}`)
    );

    return () => {
      unsubscribeCompetitions();
      unsubscribeUsers();
    };
  }, [db]);

  useEffect(() => {
    const competitionIds = new Set(competitions.map((competition) => competition.id));
    setParticipantCounts((currentCounts) =>
      Object.fromEntries(
        Object.entries(currentCounts).filter(([competitionId]) =>
          competitionIds.has(competitionId)
        )
      )
    );

    const unsubscribeParticipants = competitions.map((competition) =>
      onSnapshot(
        collection(db, `competitions/${competition.id}/participants`),
        (snapshot) =>
          setParticipantCounts((currentCounts) => ({
            ...currentCounts,
            [competition.id]: snapshot.size,
          })),
        (error) =>
          setDashboardError(
            `Unable to load participants for "${competition.name}": ${error.message}`
          )
      )
    );

    return () => {
      unsubscribeParticipants.forEach((unsubscribe) => unsubscribe());
    };
  }, [competitions, db]);

  const generateCode = async () => {
    if (!companyName.trim()) return alert("Please enter the company name.");

    const code =
      "GOLF-" + Math.random().toString(36).substring(2, 8).toUpperCase();
    await addDoc(collection(db, "access_codes"), {
      code,
      companyName: companyName.trim(),
      sponsorLimit: Number(sponsorLimit),
      used: false,
      createdAt: new Date(),
    });
    setGeneratedCode(code);
  };

  const startEditingCompetition = (competition) => {
    setEditingCompetition(competition);
    setEditData({
      name: competition.name || "",
      companyName: competition.companyName || "",
      eventLogo: competition.eventLogo || "",
      format: competition.format || "Stableford",
      defaultHandicap: competition.defaultHandicap ?? 18,
      scrambleScoringMode: competition.scrambleScoringMode || "Gross",
    });
  };

  const saveCompetition = async () => {
    if (!editingCompetition) return;
    if (!editData.name.trim()) {
      setDashboardError("Competition name is required.");
      return;
    }

    setSavingCompetition(true);
    setDashboardError("");
    try {
      const updatedData = {
        ...editData,
        name: editData.name.trim(),
        companyName: editData.companyName.trim(),
        defaultHandicap: Number(editData.defaultHandicap),
      };
      await updateDoc(doc(db, "competitions", editingCompetition.id), updatedData);
      setEditingCompetition(null);
    } catch (error) {
      setDashboardError(`Unable to save competition: ${error.message}`);
    } finally {
      setSavingCompetition(false);
    }
  };

  const deleteCompetition = async (competition) => {
    if (
      !window.confirm(
        `Delete "${competition.name}" and all of its participants and scores? This cannot be undone.`
      )
    ) {
      return;
    }

    setDashboardError("");
    try {
      const [participantsSnapshot, scoresSnapshot] = await Promise.all([
        getDocs(collection(db, `competitions/${competition.id}/participants`)),
        getDocs(collection(db, `competitions/${competition.id}/scores`)),
      ]);
      await Promise.all([
        ...participantsSnapshot.docs.map((participant) => deleteDoc(participant.ref)),
        ...scoresSnapshot.docs.map((score) => deleteDoc(score.ref)),
      ]);
      await deleteDoc(doc(db, "competitions", competition.id));
      if (editingCompetition?.id === competition.id) {
        setEditingCompetition(null);
      }
    } catch (error) {
      setDashboardError(`Unable to delete competition: ${error.message}`);
    }
  };

  const getCompetitionDate = (createdAt) => {
    const date =
      createdAt?.toDate?.() ||
      (createdAt instanceof Date ? createdAt : null);
    return date ? date.toLocaleDateString() : "Date unavailable";
  };

  return (
    <div className="bg-white p-4 rounded-lg shadow space-y-4">
      <h2 className="text-xl font-bold border-b pb-2">Owner Portal</h2>

      <section className="space-y-3">
        <div className="flex justify-between items-center border-b pb-2">
          <h3 className="text-lg font-bold">Live Competitions</h3>
          <span className="text-xs text-gray-500">
            {competitions.length} competition{competitions.length === 1 ? "" : "s"}
          </span>
        </div>
        {dashboardError && (
          <p role="alert" className="p-2 rounded bg-red-50 text-red-700 text-sm">
            {dashboardError}
          </p>
        )}
        {competitions.length === 0 ? (
          <p className="text-sm text-gray-500">
            No competitions with an organizer share code are running.
          </p>
        ) : (
          <div className="space-y-3">
            {competitions.map((competition) => {
              const organizer = organizers[competition.organizerId];
              const isEditing = editingCompetition?.id === competition.id;
              return (
                <article key={competition.id} className="p-3 border rounded bg-gray-50 space-y-3">
                  <div className="flex justify-between items-start gap-3">
                    <div className="min-w-0">
                      <p className="font-bold break-words">{competition.name || "Untitled competition"}</p>
                      <p className="text-xs text-gray-600">
                        Host: {competition.companyName || "Not specified"}
                      </p>
                      <p className="text-xs text-gray-600">
                        Organizer: {organizer?.name || organizer?.email || competition.organizerId || "Unknown"}
                      </p>
                      <p className="text-xs text-gray-600">
                        Share code: <span className="font-bold">{competition.compCode}</span>
                        {" | "}{competition.format || "Format not set"}
                      </p>
                      <p className="text-xs text-gray-600">
                        {getCompetitionDate(competition.createdAt)} |{" "}
                        {participantCounts[competition.id] ?? "…"} participant
                        {participantCounts[competition.id] === 1 ? "" : "s"}
                      </p>
                    </div>
                    <div className="flex shrink-0 gap-2">
                      <button
                        onClick={() =>
                          isEditing
                            ? setEditingCompetition(null)
                            : startEditingCompetition(competition)
                        }
                        className="bg-blue-600 text-white px-3 py-1 rounded font-bold text-xs"
                      >
                        {isEditing ? "Cancel" : "Edit"}
                      </button>
                      <button
                        onClick={() => deleteCompetition(competition)}
                        className="bg-red-600 text-white px-3 py-1 rounded font-bold text-xs"
                      >
                        Delete
                      </button>
                    </div>
                  </div>

                  {isEditing && (
                    <div className="space-y-2 border-t pt-3">
                      <input
                        aria-label="Competition name"
                        type="text"
                        placeholder="Competition name"
                        className="border p-2 rounded w-full"
                        value={editData.name}
                        onChange={(e) => setEditData({ ...editData, name: e.target.value })}
                      />
                      <input
                        aria-label="Host company"
                        type="text"
                        placeholder="Host company"
                        className="border p-2 rounded w-full"
                        value={editData.companyName}
                        onChange={(e) => setEditData({ ...editData, companyName: e.target.value })}
                      />
                      <input
                        aria-label="Event logo URL"
                        type="text"
                        placeholder="Event logo URL"
                        className="border p-2 rounded w-full"
                        value={editData.eventLogo}
                        onChange={(e) => setEditData({ ...editData, eventLogo: e.target.value })}
                      />
                      <select
                        aria-label="Tournament format"
                        className="border p-2 rounded w-full bg-white"
                        value={editData.format}
                        onChange={(e) => setEditData({ ...editData, format: e.target.value })}
                      >
                        <option value="Stroke Play / Medal">Stroke Play / Medal</option>
                        <option value="Stableford">Stableford</option>
                        <option value="Scramble">Scramble</option>
                        <option value="BBS">BBS (Better Ball Stableford)</option>
                        <option value="Fourball (Two Scores to Count)">
                          Fourball (Two Scores to Count)
                        </option>
                      </select>
                      {editData.format === "Scramble" && (
                        <select
                          aria-label="Scramble scoring mode"
                          className="border p-2 rounded w-full bg-white"
                          value={editData.scrambleScoringMode}
                          onChange={(e) =>
                            setEditData({ ...editData, scrambleScoringMode: e.target.value })
                          }
                        >
                          <option value="Gross">Gross Score</option>
                          <option value="Net">Net Score</option>
                        </select>
                      )}
                      <input
                        aria-label="Default handicap"
                        type="number"
                        placeholder="Default handicap"
                        className="border p-2 rounded w-full"
                        value={editData.defaultHandicap}
                        onChange={(e) =>
                          setEditData({ ...editData, defaultHandicap: e.target.value })
                        }
                      />
                      <button
                        onClick={saveCompetition}
                        disabled={savingCompetition}
                        className="bg-green-700 disabled:bg-green-400 text-white p-2 rounded w-full font-bold"
                      >
                        {savingCompetition ? "Saving..." : "Save Competition"}
                      </button>
                    </div>
                  )}
                </article>
              );
            })}
          </div>
        )}
      </section>

      <div>
        <label className="block text-sm font-bold mb-1">
          Company / Event Host Name:
        </label>
        <input
          type="text"
          placeholder="e.g. Acme Corporation"
          className="border p-2 rounded w-full"
          value={companyName}
          onChange={(e) => setCompanyName(e.target.value)}
        />
      </div>

      <div>
        <label className="block text-sm font-bold mb-1">
          Hole Sponsors Allowed (0-18):
        </label>
        <input
          type="number"
          max="18"
          min="0"
          value={sponsorLimit}
          onChange={(e) => setSponsorLimit(e.target.value)}
          className="border p-2 rounded w-full"
        />
      </div>

      <button
        onClick={generateCode}
        className="bg-purple-600 text-white px-4 py-2 rounded font-bold w-full shadow"
      >
        Generate Access Code
      </button>

      {generatedCode && (
        <div className="mt-4 p-4 bg-green-100 border border-green-400 rounded text-center">
          <p className="text-xs text-green-800 font-semibold mb-1">
            Access Code for {companyName}:
          </p>
          <p className="text-2xl font-black text-green-900">{generatedCode}</p>
        </div>
      )}
    </div>
  );
}

function BothDashboard({ user, db }) {
  const [mode, setMode] = useState("Player");
  return (
    <div>
      <div className="flex bg-gray-200 p-1 rounded-full mb-4">
        <button
          className={`flex-1 py-2 rounded-full font-bold ${
            mode === "Player" ? "bg-green-700 text-white" : "text-gray-500"
          }`}
          onClick={() => setMode("Player")}
        >
          Play Mode
        </button>
        <button
          className={`flex-1 py-2 rounded-full font-bold ${
            mode === "Organizer" ? "bg-green-700 text-white" : "text-gray-500"
          }`}
          onClick={() => setMode("Organizer")}
        >
          Organizer Mode
        </button>
      </div>
      {mode === "Player" ? (
        <PlayerDashboard user={user} currentUserDoc={user} db={db} />
      ) : (
        <OrganizerDashboard user={user} db={db} />
      )}
    </div>
  );
}

// --- ORGANIZER DASHBOARD ---
function OrganizerDashboard({ user, db }) {
  const [myEvents, setMyEvents] = useState([]);
  const [activeComp, setActiveComp] = useState(null);

  const [accessCode, setAccessCode] = useState("");
  const [verifiedCodeData, setVerifiedCodeData] = useState(null);
  const [compName, setCompName] = useState("");
  const [courseName, setCourseName] = useState("");
  const [eventLogo, setEventLogo] = useState("");
  const [format, setFormat] = useState("Stableford");
  const [scrambleScoringMode, setScrambleScoringMode] = useState("Gross");
  const [defaultHandicap, setDefaultHandicap] = useState(18);
  const [showCourseSetup, setShowCourseSetup] = useState(false);
  
  // Player Management State
  const [participantsList, setParticipantsList] = useState([]);
  const [showParticipants, setShowParticipants] = useState(false);
  const [editingPlayerId, setEditingPlayerId] = useState(null);
  const [editPlayerData, setEditPlayerData] = useState({});

  // TV / Projector Mode State
  const [isFullscreenBoard, setIsFullscreenBoard] = useState(false);

  const [holes, setHoles] = useState(
    Array.from({ length: 18 }, (_, i) => ({
      hole: i + 1,
      par: 4,
      mensStrokeIndex: i + 1,
      womensStrokeIndex: i + 1,
    }))
  );

  const [sponsorHole, setSponsorHole] = useState(1);
  const [sponsorName, setSponsorName] = useState("");
  const [sponsorMedia, setSponsorMedia] = useState("");

  useEffect(() => {
    const fetchMyEvents = async () => {
      const q = query(
        collection(db, "competitions"),
        where("organizerId", "==", user.id)
      );
      const snap = await getDocs(q);
      const events = [];
      snap.forEach((doc) => events.push({ id: doc.id, ...doc.data() }));
      setMyEvents(events);
    };
    fetchMyEvents();
  }, [user.id, db, activeComp]);

  const loadEventForEditing = (event) => {
    setActiveComp(event);
    setCompName(event.name);
    setCourseName(event.courseName || "");
    setEventLogo(event.eventLogo || "");
    setFormat(event.format);
    setScrambleScoringMode(event.scrambleScoringMode || "Gross");
    setDefaultHandicap(event.defaultHandicap || 18);
    setShowParticipants(false);
    if (event.holes) setHoles(normalizeCourseHoles(event.holes));
  };

  const fetchCompParticipants = async () => {
    const snap = await getDocs(collection(db, `competitions/${activeComp.id}/participants`));
    const pList = [];
    snap.forEach((d) => pList.push({ id: d.id, ...d.data() }));
    pList.sort((a, b) => a.name.localeCompare(b.name));
    setParticipantsList(pList);
  };

  const handleDeletePlayer = async (playerId) => {
    if (window.confirm("Are you sure you want to delete this player? Their scores will remain but they will be removed from the active roster.")) {
      await deleteDoc(doc(db, `competitions/${activeComp.id}/participants`, playerId));
      fetchCompParticipants();
    }
  };

  const handleSavePlayerEdit = async () => {
    await updateDoc(doc(db, `competitions/${activeComp.id}/participants`, editingPlayerId), {
       name: editPlayerData.name,
       handicap: Number(editPlayerData.handicap),
       gender: editPlayerData.gender || "",
       fourballName: editPlayerData.fourballName || "",
       teamName: editPlayerData.teamName || ""
    });
    setEditingPlayerId(null);
    fetchCompParticipants();
  };

  const deleteEvent = async (eventId) => {
    if (window.confirm("Are you sure you want to delete this event?")) {
      await deleteDoc(doc(db, "competitions", eventId));
      setMyEvents(myEvents.filter((e) => e.id !== eventId));
      if (activeComp && activeComp.id === eventId) setActiveComp(null);
    }
  };

  const verifyCode = async () => {
    const q = query(
      collection(db, "access_codes"),
      where("code", "==", accessCode.toUpperCase()),
      where("used", "==", false)
    );
    const snap = await getDocs(q);
    if (!snap.empty) {
      const data = snap.docs[0].data();
      setVerifiedCodeData({ id: snap.docs[0].id, ...data });
      setCompName(`${data.companyName} Golf Day`);
      setCourseName("");
      setHoles(
        Array.from({ length: 18 }, (_, i) => ({
          hole: i + 1,
          par: 4,
          mensStrokeIndex: i + 1,
          womensStrokeIndex: i + 1,
        }))
      );
      setShowCourseSetup(false);
    } else {
      alert("Invalid or already used Access Code.");
    }
  };

  const updateHole = (index, field, value) => {
    const newHoles = [...holes];
    newHoles[index][field] = Number(value);
    setHoles(newHoles);
  };

  const createEvent = async () => {
    try {
      const compCode = Math.random().toString(36).substring(2, 8).toUpperCase();
      const newComp = {
        name: compName,
        courseName: courseName.trim().replace(/\s+/g, " "),
        eventLogo,
        companyName: verifiedCodeData.companyName,
        format,
        scrambleScoringMode,
        compCode,
        defaultHandicap: Number(defaultHandicap),
        organizerId: user.id,
        sponsorLimit: verifiedCodeData.sponsorLimit,
        holes,
        sponsors: {},
        participantsIds: [],
        createdAt: new Date(),
      };
      const compRef = await addDoc(collection(db, "competitions"), newComp);
      await updateDoc(doc(db, "access_codes", verifiedCodeData.id), {
        used: true,
      });
      loadEventForEditing({ id: compRef.id, ...newComp });
    } catch (error) {
      alert(`Unable to create event: ${error.message}`);
      return;
    }
  };

  const saveEventUpdates = async () => {
    try {
      const updatedData = {
        name: compName,
        courseName: courseName.trim().replace(/\s+/g, " "),
        eventLogo,
        format,
        scrambleScoringMode,
        defaultHandicap: Number(defaultHandicap),
        holes,
      };
      await updateDoc(doc(db, "competitions", activeComp.id), updatedData);
      setActiveComp({ ...activeComp, ...updatedData });
    } catch (error) {
      alert(`Unable to save event settings: ${error.message}`);
      return;
    }
    alert("Event settings saved successfully!");
  };

  const assignSponsor = async () => {
    if (!sponsorName.trim()) return;
    const currentSponsorsCount = Object.keys(activeComp.sponsors || {}).length;
    if (
      currentSponsorsCount >= activeComp.sponsorLimit &&
      !activeComp.sponsors[sponsorHole]
    ) {
      alert(`Sponsor limit reached!`);
      return;
    }
    const updatedSponsors = {
      ...activeComp.sponsors,
      [sponsorHole]: { name: sponsorName, mediaUrl: sponsorMedia },
    };
    await updateDoc(doc(db, "competitions", activeComp.id), {
      sponsors: updatedSponsors,
    });
    setActiveComp({ ...activeComp, sponsors: updatedSponsors });
    setSponsorName("");
    setSponsorMedia("");
  };

  const downloadLeaderboardCSV = async () => {
    try {
      const pSnap = await getDocs(
        collection(db, `competitions/${activeComp.id}/participants`)
      );
      const sSnap = await getDocs(
        collection(db, `competitions/${activeComp.id}/scores`)
      );

      const pList = [];
      pSnap.forEach((d) => pList.push({ id: d.id, ...d.data() }));
      const sList = [];
      sSnap.forEach((d) => sList.push(d.data()));

      const data = buildLeaderboardData(activeComp, pList, sList);

      let csv = "sep=,\n";
      csv += `"Event Name","Host Company","Tournament Format","Logo URL"\n`;
      csv += `"${activeComp.name}","${activeComp.companyName || ""}","${
        activeComp.format
      }","${activeComp.eventLogo || ""}"\n\n`;

      if (data.teamFormat) {
        if (activeComp.format === "Scramble" && activeComp.scrambleScoringMode === "Net") {
          csv += `"Position","Team Name","Members","Team HCP","Total Net","Total Gross"\n`;
          data.board.forEach((t, index) => {
            csv += `"${index + 1}","${t.name}","${t.members.join(", ")}","${t.teamHcp}","${t.totalNet}","${t.totalGross}"\n`;
          });
        } else {
          csv += `"Position","Team Name","Members","Total ${
            data.isPointsFormat ? "Points" : "Gross Strokes"
          }"\n`;
          data.board.forEach((t, index) => {
            csv += `"${index + 1}","${t.name}","${t.members.join(", ")}","${
              data.isPointsFormat ? t.totalPoints : t.totalGross
            }"\n`;
          });
        }
      } else {
        csv += `"Position","Player","Handicap","Total Gross","Total Points"\n`;
        data.board.forEach((p, index) => {
          csv += `"${index + 1}","${p.name}","${p.handicap}","${p.totalGross}","${p.totalPoints}"\n`;
        });
      }

      const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${activeComp.name.replace(/\s+/g, "_")}_Prize_Giving.csv`;
      a.click();
    } catch (err) {
      alert("Error generating CSV: " + err.message);
    }
  };

  if (isFullscreenBoard && activeComp) {
    return (
      <div className="fixed inset-0 z-50 bg-gray-900 overflow-y-auto flex flex-col font-sans">
        <div className="bg-gray-800 p-4 flex justify-between items-center shadow-lg sticky top-0 z-10 border-b-4 border-green-600">
          <div className="flex items-center gap-4">
            {activeComp.eventLogo && (
              <img src={activeComp.eventLogo} alt="Logo" className="h-12 bg-white rounded p-1 object-contain" />
            )}
            <div>
              <h1 className="text-2xl sm:text-4xl font-bold text-white uppercase tracking-wider">{activeComp.name}</h1>
              <p className="text-gray-400 font-semibold tracking-wide text-sm">{activeComp.format}</p>
            </div>
          </div>
          <button onClick={() => setIsFullscreenBoard(false)} className="bg-red-600 hover:bg-red-700 text-white px-6 py-2 rounded-lg font-bold text-lg shadow-lg">
            Exit TV Mode
          </button>
        </div>
        <div className="p-4 sm:p-8 flex-1 max-w-7xl mx-auto w-full">
          <Leaderboard comp={activeComp} db={db} tvMode={true} />
        </div>
      </div>
    );
  }

  if (activeComp)
    return (
      <div className="bg-white p-4 rounded shadow space-y-6">
        <div className="flex justify-between items-start border-b pb-4">
          <div className="flex-1">
            {activeComp.eventLogo && (
              <img
                src={activeComp.eventLogo}
                alt="Event Logo"
                className="h-10 mb-2 object-contain"
              />
            )}
            <h2 className="text-2xl font-bold text-green-800">
              {activeComp.name}
            </h2>
            {activeComp.companyName && (
              <p className="text-xs font-bold text-gray-500 uppercase tracking-wide">
                Host: {activeComp.companyName}
              </p>
            )}
            <p className="text-gray-600 mt-1">
              Share Code:{" "}
              <span className="font-bold text-xl text-green-600">
                {activeComp.compCode}
              </span>
            </p>
          </div>
          <button
            onClick={() => setActiveComp(null)}
            className="bg-gray-200 text-gray-700 px-3 py-1 rounded text-sm font-bold hover:bg-gray-300"
          >
            &larr; Back
          </button>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <button
            onClick={downloadLeaderboardCSV}
            className="bg-indigo-600 text-white p-3 rounded font-bold shadow-md hover:bg-indigo-700 uppercase tracking-wide text-xs"
          >
            📥 Download CSV
          </button>
          <button
            onClick={() => setIsFullscreenBoard(true)}
            className="bg-gray-900 text-white p-3 rounded font-bold shadow-md hover:bg-black uppercase tracking-wide text-xs border-b-4 border-green-500"
          >
            📺 Launch TV Mode
          </button>
        </div>

        <CompetitionMessages comp={activeComp} db={db} user={user} canSend />

        <div className="bg-gray-50 p-4 border rounded space-y-4">
          <h3 className="font-bold text-lg mb-2">Edit Event Settings</h3>
          <input
            type="text"
            placeholder="Event Name"
            className="border p-2 rounded w-full"
            value={compName}
            onChange={(e) => setCompName(e.target.value)}
          />
          <label className="block text-sm font-bold">
            Golf Course
            <input
              type="text"
              className="border p-2 rounded w-full font-normal"
              placeholder="Enter a course name"
              value={courseName}
              onChange={(event) => setCourseName(event.target.value)}
            />
          </label>
          <input
            type="text"
            placeholder="Event / Company Logo URL (JPEG, PNG, etc.)"
            className="border p-2 rounded w-full"
            value={eventLogo}
            onChange={(e) => setEventLogo(e.target.value)}
          />

          <div className="flex gap-2">
            <select
              className="border p-2 rounded w-1/2 bg-white"
              value={format}
              onChange={(e) => setFormat(e.target.value)}
            >
              <option value="Stroke Play / Medal">Stroke Play / Medal</option>
              <option value="Stableford">Stableford</option>
              <option value="Scramble">Scramble</option>
              <option value="BBS">BBS (Better Ball Stableford)</option>
              <option value="Fourball (Two Scores to Count)">
                Fourball (Two Scores to Count)
              </option>
            </select>
            <input
              type="number"
              placeholder="Def. Handicap"
              className="border p-2 rounded w-1/2"
              value={defaultHandicap}
              onChange={(e) => setDefaultHandicap(e.target.value)}
            />
          </div>

          {format === "Scramble" && (
            <div className="bg-yellow-50 p-3 rounded border border-yellow-200">
              <label className="text-xs font-bold text-gray-700 block mb-1">
                Scramble Scoring Mode
              </label>
              <select
                className="w-full border p-2 rounded bg-white text-sm"
                value={scrambleScoringMode}
                onChange={(e) => setScrambleScoringMode(e.target.value)}
              >
                <option value="Gross">Gross Score (Actual Strokes)</option>
                <option value="Net">Net Score (Uses Average Team Handicap)</option>
              </select>
              {scrambleScoringMode === "Net" && (
                 <p className="text-[10px] text-gray-500 mt-1 italic">
                   System automatically calculates team handicap using: 25% Lowest, 20% 2nd, 15% 3rd, 10% Highest.
                 </p>
              )}
            </div>
          )}

          <button
            onClick={() => setShowCourseSetup(!showCourseSetup)}
            className="w-full bg-white border border-gray-300 p-2 rounded font-bold text-gray-700"
          >
            {showCourseSetup
              ? "Hide Course Setup"
              : "Edit Course (Par & Men's & Women's Stroke Index)"}
          </button>

          {showCourseSetup && (
            <div className="max-h-64 overflow-y-auto border rounded p-2 bg-white">
              <div className="grid grid-cols-4 gap-2 font-bold text-xs mb-2 text-center">
                <span>Hole</span>
                <span>Par</span>
                <span>Men's SI</span>
                <span>Women's SI</span>
              </div>
              {holes.map((h, i) => (
                <div key={h.hole} className="grid grid-cols-4 gap-2 mb-2">
                  <div className="flex items-center justify-center font-bold">
                    {h.hole}
                  </div>
                  <input
                    type="number"
                    className="border p-1 text-center rounded bg-gray-50"
                    value={h.par}
                    onChange={(e) => updateHole(i, "par", e.target.value)}
                  />
                  <input
                    type="number"
                    className="border p-1 text-center rounded bg-gray-50"
                    min="1"
                    max="18"
                    aria-label={`Men's stroke index for hole ${h.hole}`}
                    value={h.mensStrokeIndex ?? h.strokeIndex ?? h.hole}
                    onChange={(e) =>
                      updateHole(i, "mensStrokeIndex", e.target.value)
                    }
                  />
                  <input
                    type="number"
                    className="border p-1 text-center rounded bg-gray-50"
                    min="1"
                    max="18"
                    aria-label={`Women's stroke index for hole ${h.hole}`}
                    value={h.womensStrokeIndex ?? h.strokeIndex ?? h.hole}
                    onChange={(e) =>
                      updateHole(i, "womensStrokeIndex", e.target.value)
                    }
                  />
                </div>
              ))}
            </div>
          )}

          <button
            onClick={saveEventUpdates}
            className="bg-blue-600 text-white p-2 rounded w-full font-bold shadow"
          >
            Save Updates
          </button>
        </div>

        <div className="bg-gray-50 p-4 border rounded">
          <h3 className="font-bold text-lg mb-2">
            Manage Sponsors ({Object.keys(activeComp.sponsors || {}).length} /{" "}
            {activeComp.sponsorLimit})
          </h3>
          <div className="space-y-3 mb-4 p-3 bg-white border rounded">
            <select
              className="border p-2 rounded w-full bg-white"
              value={sponsorHole}
              onChange={(e) => setSponsorHole(e.target.value)}
            >
              {Array.from({ length: 18 }, (_, i) => (
                <option key={i + 1} value={i + 1}>
                  Hole {i + 1}
                </option>
              ))}
            </select>
            <input
              type="text"
              placeholder="Brand Name"
              className="border p-2 rounded w-full"
              value={sponsorName}
              onChange={(e) => setSponsorName(e.target.value)}
            />
            <input
              type="text"
              placeholder="Image URL (JPEG/PNG) or Video URL (.mp4/.mpeg)"
              className="border p-2 rounded w-full text-sm"
              value={sponsorMedia}
              onChange={(e) => setSponsorMedia(e.target.value)}
            />
            <button
              onClick={assignSponsor}
              className="bg-green-700 text-white p-2 rounded font-bold w-full shadow"
            >
              Add / Update Sponsor
            </button>
          </div>
        </div>

        {/* --- MANAGE PLAYERS SECTION --- */}
        <div className="bg-gray-50 p-4 border rounded">
          <div className="flex justify-between items-center mb-2">
            <h3 className="font-bold text-lg">Manage Players & Fourballs</h3>
            <button 
               onClick={() => {
                 setShowParticipants(!showParticipants);
                 if (!showParticipants) fetchCompParticipants();
               }} 
               className="text-xs bg-gray-300 hover:bg-gray-400 text-gray-800 px-3 py-1 rounded font-bold"
            >
              {showParticipants ? "Hide List" : "Load Players"}
            </button>
          </div>
          
          {showParticipants && (
            <div className="space-y-3 mt-3 max-h-96 overflow-y-auto pr-1">
              {participantsList.length === 0 ? (
                <p className="text-sm text-gray-500">No players registered yet.</p>
              ) : (
                participantsList.map(p => (
                  <div key={p.id} className="bg-white p-3 border rounded shadow-sm">
                    {editingPlayerId === p.id ? (
                      <div className="space-y-2 text-sm">
                        <div>
                          <label className="text-[10px] text-gray-500 font-bold uppercase block">Player Name</label>
                          <input value={editPlayerData.name} onChange={e => setEditPlayerData({...editPlayerData, name: e.target.value})} className="border p-1 w-full rounded" />
                        </div>
                        <div>
                          <label className="text-[10px] text-gray-500 font-bold uppercase block">Handicap</label>
                          <input type="number" value={editPlayerData.handicap} onChange={e => setEditPlayerData({...editPlayerData, handicap: e.target.value})} className="border p-1 w-full rounded" />
                        </div>
                        <div>
                          <label className="text-[10px] text-gray-500 font-bold uppercase block">Stroke Index Tee</label>
                          <select value={editPlayerData.gender || ""} onChange={e => setEditPlayerData({...editPlayerData, gender: e.target.value})} className="border p-1 w-full rounded bg-white">
                            <option value="">Not selected</option>
                            <option value="Man">Men's tees</option>
                            <option value="Woman">Women's tees</option>
                          </select>
                        </div>
                        <div>
                          <label className="text-[10px] text-gray-500 font-bold uppercase block">Fourball Name (Grouping)</label>
                          <input value={editPlayerData.fourballName || ""} onChange={e => setEditPlayerData({...editPlayerData, fourballName: e.target.value})} className="border p-1 w-full rounded" placeholder="e.g. Flight 1" />
                        </div>
                        <div>
                          <label className="text-[10px] text-gray-500 font-bold uppercase block">Team Name (Scoreboard Team)</label>
                          <input value={editPlayerData.teamName || ""} onChange={e => setEditPlayerData({...editPlayerData, teamName: e.target.value})} className="border p-1 w-full rounded" placeholder="e.g. Individual or The Eagles" />
                        </div>
                        <div className="flex gap-2 pt-2">
                          <button onClick={handleSavePlayerEdit} className="bg-green-600 hover:bg-green-700 text-white px-4 py-1.5 rounded font-bold text-xs">Save</button>
                          <button onClick={() => setEditingPlayerId(null)} className="bg-gray-400 hover:bg-gray-500 text-white px-4 py-1.5 rounded font-bold text-xs">Cancel</button>
                        </div>
                      </div>
                    ) : (
                      <div className="flex justify-between items-center">
                        <div>
                          <p className="font-bold text-sm">{p.name} <span className="text-gray-500 font-normal text-xs">(HCP: {p.handicap})</span></p>
                          <p className="text-[10px] text-gray-500 uppercase mt-1 tracking-wide">
                            Fourball: <span className="font-bold text-gray-800">{p.fourballName || 'None'}</span> | 
                            Team: <span className="font-bold text-gray-800">{p.teamName || 'None'}</span>
                          </p>
                        </div>
                        <div className="flex flex-col gap-1.5">
                          <button onClick={() => { setEditingPlayerId(p.id); setEditPlayerData(p); }} className="bg-blue-50 hover:bg-blue-100 border border-blue-200 text-blue-700 px-3 py-1 rounded text-xs font-bold">Edit</button>
                          <button onClick={() => handleDeletePlayer(p.id)} className="bg-red-50 hover:bg-red-100 border border-red-200 text-red-700 px-3 py-1 rounded text-xs font-bold">Delete</button>
                        </div>
                      </div>
                    )}
                  </div>
                ))
              )}
            </div>
          )}
        </div>

        <Leaderboard comp={activeComp} db={db} />
      </div>
    );

  return (
    <div className="space-y-6">
      <div className="bg-white p-4 rounded shadow">
        <h2 className="text-xl font-bold mb-4 border-b pb-2">My Live Events</h2>
        {myEvents.length === 0 ? (
          <p className="text-gray-500 text-sm">You have no active events.</p>
        ) : (
          <div className="space-y-2">
            {myEvents.map((event) => (
              <div
                key={event.id}
                className="flex justify-between items-center bg-gray-50 p-3 border rounded"
              >
                <div>
                  <p className="font-bold">{event.name}</p>
                  {event.companyName && (
                    <p className="text-xs text-gray-500 font-semibold">
                      Host: {event.companyName}
                    </p>
                  )}
                  <p className="text-xs text-gray-500">
                    Code: {event.compCode} | {event.format}
                  </p>
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => loadEventForEditing(event)}
                    className="bg-green-700 text-white px-3 py-1 rounded font-bold text-sm"
                  >
                    Manage
                  </button>
                  <button
                    onClick={() => deleteEvent(event.id)}
                    className="bg-red-600 text-white px-3 py-1 rounded font-bold text-sm"
                  >
                    Delete
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="bg-white p-4 rounded shadow">
        <h2 className="text-xl font-bold mb-4 border-b pb-2">
          Start New Golf Day
        </h2>
        {!verifiedCodeData ? (
          <div className="space-y-2">
            <input
              type="text"
              placeholder="Enter Owner Access Code"
              className="border p-2 rounded w-full uppercase"
              value={accessCode}
              onChange={(e) => setAccessCode(e.target.value)}
            />
            <button
              onClick={verifyCode}
              className="bg-blue-600 text-white p-2 rounded w-full font-bold"
            >
              Verify Access Code
            </button>
          </div>
        ) : (
          <div className="space-y-4">
            <p className="text-green-700 text-sm font-bold bg-green-50 p-2 rounded border border-green-200">
              Verified for{" "}
              <span className="underline">{verifiedCodeData.companyName}</span>{" "}
              ({verifiedCodeData.sponsorLimit} holes)
            </p>
            <input
              type="text"
              placeholder="Event Name"
              className="border p-2 rounded w-full font-semibold"
              value={compName}
              onChange={(e) => setCompName(e.target.value)}
            />
            <input
              type="text"
              placeholder="Golf Course"
              className="border p-2 rounded w-full font-semibold"
              value={courseName}
              onChange={(event) => setCourseName(event.target.value)}
            />
            <input
              type="text"
              placeholder="Event / Company Logo URL (JPEG, PNG, etc.)"
              className="border p-2 rounded w-full text-sm"
              value={eventLogo}
              onChange={(e) => setEventLogo(e.target.value)}
            />

            <select
              className="border p-2 rounded w-full bg-white"
              value={format}
              onChange={(e) => setFormat(e.target.value)}
            >
              <option value="Stroke Play / Medal">Stroke Play / Medal</option>
              <option value="Stableford">Stableford</option>
              <option value="Scramble">Scramble</option>
              <option value="BBS">BBS (Better Ball Stableford)</option>
              <option value="Fourball (Two Scores to Count)">
                Fourball (Two Scores to Count)
              </option>
            </select>

            {format === "Scramble" && (
              <div className="bg-yellow-50 p-3 rounded border border-yellow-200">
                <label className="text-xs font-bold text-gray-700 block mb-1">
                  Scramble Scoring Mode
                </label>
                <select
                  className="w-full border p-2 rounded bg-white text-sm"
                  value={scrambleScoringMode}
                  onChange={(e) => setScrambleScoringMode(e.target.value)}
                >
                  <option value="Gross">Gross Score (Actual Strokes)</option>
                  <option value="Net">Net Score (Uses Average Team Handicap)</option>
                </select>
              </div>
            )}

            <input
              type="number"
              placeholder="Default Handicap"
              className="border p-2 rounded w-full"
              value={defaultHandicap}
              onChange={(e) => setDefaultHandicap(e.target.value)}
            />

            <button
              onClick={() => setShowCourseSetup(!showCourseSetup)}
              className="w-full bg-gray-200 p-2 rounded font-bold text-gray-700"
            >
              Configure Course (Par & Men's & Women's Stroke Index)
            </button>
            {showCourseSetup && (
              <div className="max-h-64 overflow-y-auto border rounded p-2 bg-gray-50">
                <div className="grid grid-cols-4 gap-2 font-bold text-xs mb-2 text-center">
                  <span>Hole</span>
                  <span>Par</span>
                  <span>Men's SI</span>
                  <span>Women's SI</span>
                </div>
                {holes.map((h, i) => (
                  <div key={h.hole} className="grid grid-cols-4 gap-2 mb-2">
                    <div className="flex items-center justify-center font-bold">
                      {h.hole}
                    </div>
                    <input
                      type="number"
                      className="border p-1 text-center rounded bg-white"
                      value={h.par}
                      onChange={(e) => updateHole(i, "par", e.target.value)}
                    />
                    <input
                      type="number"
                      className="border p-1 text-center rounded bg-white"
                      min="1"
                      max="18"
                      aria-label={`Men's stroke index for hole ${h.hole}`}
                      value={h.mensStrokeIndex ?? h.strokeIndex ?? h.hole}
                      onChange={(e) =>
                        updateHole(i, "mensStrokeIndex", e.target.value)
                      }
                    />
                    <input
                      type="number"
                      className="border p-1 text-center rounded bg-white"
                      min="1"
                      max="18"
                      aria-label={`Women's stroke index for hole ${h.hole}`}
                      value={h.womensStrokeIndex ?? h.strokeIndex ?? h.hole}
                      onChange={(e) =>
                        updateHole(i, "womensStrokeIndex", e.target.value)
                      }
                    />
                  </div>
                ))}
              </div>
            )}
            <button
              onClick={createEvent}
              className="bg-blue-600 text-white p-3 rounded w-full font-bold text-lg mt-4 shadow"
            >
              Launch Event
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

// --- PLAYER DASHBOARD ---
function PlayerDashboard({ currentUserDoc, db }) {
  const [compCode, setCompCode] = useState("");
  const [activeComp, setActiveComp] = useState(null);
  const [players, setPlayers] = useState([]);
  
  const [registeredParticipants, setRegisteredParticipants] = useState([]);
  const [selectedUserId, setSelectedUserId] = useState("");
  const [history, setHistory] = useState([]);
  const [isEditingTeam, setIsEditingTeam] = useState(false);
  
  const [existingFourballs, setExistingFourballs] = useState([]);
  const [selectedFourballName, setSelectedFourballName] = useState("");

  useEffect(() => {
    const fetchHistory = async () => {
      const qHistory = query(
        collection(db, "competitions"),
        where("participantsIds", "array-contains", currentUserDoc.id)
      );
      const snapHistory = await getDocs(qHistory);
      const historyList = [];
      snapHistory.forEach((doc) =>
        historyList.push({ id: doc.id, ...doc.data() })
      );
      historyList.sort(
        (a, b) => b.createdAt?.toMillis() - a.createdAt?.toMillis()
      );
      setHistory(historyList);
    };
    fetchHistory();
  }, [currentUserDoc.id, db, activeComp]);

  const fetchParticipantsForComp = async (comp) => {
    const updatedCompSnap = await getDocs(
      collection(db, `competitions/${comp.id}/participants`)
    );
    const participantsList = [];
    const fourballSet = new Set();
    
    updatedCompSnap.forEach((d) => {
      const dat = d.data();
      participantsList.push({ id: d.id, ...dat });
      if (dat.fourballName) fourballSet.add(dat.fourballName);
    });
    
    setRegisteredParticipants(participantsList);
    setExistingFourballs(Array.from(fourballSet));
    return participantsList;
  };

  const joinCompetitionAndFetchParticipants = async () => {
    const q = query(
      collection(db, "competitions"),
      where("compCode", "==", compCode.toUpperCase())
    );
    const snap = await getDocs(q);
    if (!snap.empty) {
      const compDoc = snap.docs[0];
      const comp = { id: compDoc.id, ...compDoc.data() };

      await updateDoc(doc(db, "competitions", comp.id), {
        participantsIds: arrayUnion(currentUserDoc.id),
      });

      const allParts = await fetchParticipantsForComp(comp);
      const existingMe = allParts.find(p => p.id === currentUserDoc.id);

      if (existingMe && existingMe.fourballName) {
        const groupMembers = allParts.filter(p => p.fourballName === existingMe.fourballName);
        setPlayers(groupMembers);
        setActiveComp(comp);
        setIsEditingTeam(groupMembers.some((player) => !player.gender));
      } else {
        setActiveComp(comp);
        setPlayers([{
          id: currentUserDoc.id,
          name: currentUserDoc.name,
          handicap: currentUserDoc.handicap ?? comp.defaultHandicap ?? "",
          gender: existingMe?.gender || "",
          role: currentUserDoc.role || 'Player',
          fourballName: '',
          teamName: comp.format === 'Stableford' ? 'Individual' : ''
        }]);
        setIsEditingTeam(true);
      }
    } else {
      alert("Competition code not found!");
    }
  };

  const joinExistingFourball = async () => {
    if (!selectedFourballName) return alert("Please select a fourball group.");
    const gender = players.find((p) => p.id === currentUserDoc.id)?.gender;
    if (!gender) {
      alert("Please select whether you play off the men's or women's stroke indexes.");
      return;
    }
    const handicap = getPlayerHandicap(
      players.find((p) => p.id === currentUserDoc.id)
    );
    if (handicap === null) {
      alert("Please enter your handicap for this competition.");
      return;
    }

    const groupMembers = registeredParticipants.filter(p => p.fourballName === selectedFourballName);
    
    const updatedMe = {
      id: currentUserDoc.id,
      name: currentUserDoc.name,
      handicap,
      gender,
      role: currentUserDoc.role || 'Player',
      fourballName: selectedFourballName,
      teamName: activeComp.format === 'Stableford' ? 'Individual' : (groupMembers[0]?.teamName || ''),
    };

    await setDoc(doc(db, `competitions/${activeComp.id}/participants`, currentUserDoc.id), updatedMe, { merge: true });
    
    const allGroup = [...groupMembers.filter(p => p.id !== currentUserDoc.id), updatedMe];
    setPlayers(allGroup);
    setIsEditingTeam(allGroup.some((player) => !player.gender));
  };

  const addPartner = () => {
    if (!selectedUserId) return;
    if (players.find((p) => p.id === selectedUserId))
      return alert("Player already added.");
    if (players.length >= 4) return alert("Maximum 4 players per fourball.");

    const partner = registeredParticipants.find((u) => u.id === selectedUserId);
    if (partner) {
      setPlayers([...players, partner]);
      setSelectedUserId("");
    }
  };

  const removePartner = async (partnerId) => {
    setPlayers(players.filter((p) => p.id !== partnerId));
  };

  const updatePlayerParam = (id, field, val) => {
    setPlayers((currentPlayers) => {
      const playerExists = currentPlayers.some((p) => p.id === id);
      if (playerExists) {
        return currentPlayers.map((p) => (p.id === id ? { ...p, [field]: val } : p));
      }
      if (id !== currentUserDoc.id) return currentPlayers;
      return [
        ...currentPlayers,
        {
          id,
          name: currentUserDoc.name,
          role: currentUserDoc.role || "Player",
          handicap: field === "handicap" ? val : currentUserDoc.handicap ?? activeComp?.defaultHandicap ?? "",
          gender: field === "gender" ? val : "",
          fourballName: field === "fourballName" ? val : "",
          teamName: activeComp?.format === "Stableford" ? "Individual" : "",
        },
      ];
    });
  };

  const updateTeamNameAll = (val) => {
    setPlayers(players.map((p) => ({ ...p, fourballName: val, teamName: val })));
  };

  const getPlayerHandicap = (player) => {
    if (!player || player.handicap === "" || player.handicap == null) return null;
    const handicap = Number(player.handicap);
    return Number.isFinite(handicap) ? handicap : null;
  };

  const saveTeamAndStartScoring = async () => {
    if (players.some((player) => !player.gender)) {
      alert("Please select a gender for each player so the correct stroke indexes are used.");
      return;
    }
    if (getPlayerHandicap(players.find((p) => p.id === currentUserDoc.id)) === null) {
      alert("Please enter your handicap for this competition.");
      return;
    }

    const primaryFourballName = players[0]?.fourballName || 'My Fourball';
    const playerIds = players.map((p) => p.id);
    
    await updateDoc(doc(db, "competitions", activeComp.id), {
      participantsIds: arrayUnion(...playerIds),
    });

    for (const p of players) {
      await setDoc(
        doc(db, `competitions/${activeComp.id}/participants`, p.id),
        {
          name: p.name,
          handicap: getPlayerHandicap(p) ?? activeComp.defaultHandicap ?? 0,
          gender: p.gender,
          role: p.role || "Player",
          fourballName: primaryFourballName,
          teamName: activeComp.format === 'Stableford' ? 'Individual' : (p.teamName || primaryFourballName),
        },
        { merge: true }
      );
    }

    setActiveComp({ ...activeComp });
    setIsEditingTeam(false);
  };

  const resumeRound = async (comp) => {
    const pList = await fetchParticipantsForComp(comp);
    const myRecord = pList.find(p => p.id === currentUserDoc.id);
    
    if (myRecord && myRecord.fourballName) {
      const groupMembers = pList.filter(p => p.fourballName === myRecord.fourballName);
      setPlayers(groupMembers);
    } else {
      setPlayers(myRecord ? [myRecord] : [{
        id: currentUserDoc.id,
        name: currentUserDoc.name,
        handicap: currentUserDoc.handicap ?? comp.defaultHandicap ?? "",
        gender: "",
        role: currentUserDoc.role || "Player",
        fourballName: "",
        teamName: comp.format === "Stableford" ? "Individual" : "",
      }]);
    }

    setActiveComp(comp);
    const activeGroup = myRecord?.fourballName
      ? pList.filter((player) => player.fourballName === myRecord.fourballName)
      : myRecord
        ? [myRecord]
        : [];
    setIsEditingTeam(activeGroup.some((player) => !player.gender));
  };

  const availablePartners = registeredParticipants.filter(
    (p) => p.id && !players.some((pl) => pl.id === p.id)
  );

  if (activeComp && isEditingTeam) {
    return (
      <div className="bg-white p-4 rounded shadow space-y-4">
        <div className="flex justify-between items-center border-b pb-2">
          <h2 className="text-xl font-bold">Fourball Setup</h2>
          <button
            onClick={() => setActiveComp(null)}
            className="text-gray-500 font-bold text-sm"
          >
            &larr; Back
          </button>
        </div>

        {activeComp.eventLogo && (
          <div className="flex justify-center mb-2">
            <img
              src={activeComp.eventLogo}
              alt="Event Logo"
              className="h-16 object-contain rounded p-1 bg-white shadow-sm"
            />
          </div>
        )}

        <div className="bg-green-50 p-3 rounded border border-green-200">
          <label className="text-xs font-bold text-green-900 block mb-1">
            Stroke Index Tee
          </label>
          <select
            required
            className="border p-2 rounded w-full bg-white mb-3"
            value={players.find((p) => p.id === currentUserDoc.id)?.gender ?? ""}
            onChange={(e) =>
              updatePlayerParam(currentUserDoc.id, "gender", e.target.value)
            }
          >
            <option value="">-- Select men's or women's tees --</option>
            <option value="Man">Men's tees</option>
            <option value="Woman">Women's tees</option>
          </select>
          <label className="text-xs font-bold text-green-900 block mb-1">
            Your Handicap for This Competition
          </label>
          <input
            type="number"
            step="0.1"
            required
            className="border p-2 rounded w-full bg-white"
            value={players.find((p) => p.id === currentUserDoc.id)?.handicap ?? ""}
            onChange={(e) =>
              updatePlayerParam(currentUserDoc.id, "handicap", e.target.value)
            }
          />
        </div>

        {existingFourballs.length > 0 && (
          <div className="bg-blue-50 p-3 rounded border border-blue-200 space-y-2">
            <label className="text-xs font-bold text-blue-900 block">Or Join an Existing Fourball Created by a Teammate:</label>
            <div className="flex gap-2">
              <select className="border p-2 rounded flex-1 bg-white text-sm" value={selectedFourballName} onChange={e=>setSelectedFourballName(e.target.value)}>
                <option value="">-- Select Existing Fourball --</option>
                {existingFourballs.map(fb => <option key={fb} value={fb}>{fb}</option>)}
              </select>
              <button onClick={joinExistingFourball} className="bg-blue-600 text-white px-3 py-1 rounded font-bold text-sm">Join</button>
            </div>
          </div>
        )}

        <div className="bg-gray-50 p-4 border rounded space-y-3">
          <p className="text-xs font-bold text-gray-700">Option 2: Create a New Fourball (First Member)</p>
          
          {activeComp.format !== 'Scramble' && activeComp.format !== 'Fourball (Two Scores to Count)' && (
            <div>
              <label className="text-xs text-gray-500 font-bold block mb-1">Fourball Group Name</label>
              <input 
                type="text" 
                placeholder="e.g. Flight 1" 
                className="border p-2 text-sm rounded w-full font-semibold" 
                value={players[0]?.fourballName || ''} 
                onChange={(e) => updatePlayerParam(currentUserDoc.id, 'fourballName', e.target.value)}
              />
            </div>
          )}

          {(activeComp.format === 'Scramble' || activeComp.format === 'Fourball (Two Scores to Count)') && (
             <div className="bg-yellow-50 p-2 border border-yellow-200 rounded">
               <label className="text-xs text-yellow-900 font-bold block mb-1">
                 {activeComp.format === 'Scramble' ? 'Scramble Team Name (Required)' : 'Fourball Team Name (Required)'}
               </label>
               <input 
                 type="text" 
                 placeholder="e.g. The Eagles" 
                 className="border border-yellow-400 p-2 text-sm rounded w-full font-semibold" 
                 value={players[0]?.teamName || ''} 
                 onChange={(e) => updateTeamNameAll(e.target.value)} 
               />
             </div>
          )}

          <div className="pt-2">
            <div className="flex justify-between items-center mb-1">
              <label className="text-xs font-bold text-gray-500">Add Teammates:</label>
              <button
                type="button"
                onClick={() => fetchParticipantsForComp(activeComp)}
                className="text-xs text-blue-700 font-bold hover:underline"
              >
                Refresh players
              </button>
            </div>
            <div className="flex gap-2">
              <select
                className="border p-2 rounded flex-1 bg-white text-sm"
                value={selectedUserId}
                onChange={(e) => setSelectedUserId(e.target.value)}
              >
                <option value="">-- Select Registered Player --</option>
                {availablePartners.map((u) => (
                  <option key={u.id} value={u.id}>
                    {u.name} (HCP: {u.handicap}){u.gender ? ` (${getStrokeIndexTeeLabel(u.gender)})` : ""}
                  </option>
                ))}
              </select>
              <button
                onClick={addPartner}
                className="bg-green-700 text-white px-4 rounded font-bold"
              >
                +
              </button>
            </div>
            {availablePartners.length === 0 ? (
              <p className="text-xs text-gray-500 mt-2">
                No other registered players found yet. Refresh after they join and save their competition setup.
              </p>
            ) : (
              <p className="text-xs text-gray-500 mt-2">
                Registered players: {availablePartners.map((p) => p.name).join(", ")}
              </p>
            )}
          </div>

          <ul className="space-y-3 pt-2">
            {players.map((p) => (
              <li
                key={p.id}
                className="bg-white p-3 border rounded text-sm shadow-sm space-y-2"
              >
                <div className="flex justify-between items-center">
                  <div>
                    <span className="font-bold">{p.name}</span> {p.id === currentUserDoc.id && <span className="text-xs text-green-700 font-bold">(You)</span>}
                    <p className="text-xs text-gray-500">HCP: {p.handicap} | {p.gender ? getStrokeIndexTeeLabel(p.gender) : "Stroke index tee not selected"}</p>
                  </div>
                  {p.id !== currentUserDoc.id && (
                    <button
                      onClick={() => removePartner(p.id)}
                      className="text-red-600 text-xs font-bold px-2 py-1 bg-red-50 rounded border border-red-200"
                    >
                      Remove
                    </button>
                  )}
                </div>

                <div>
                  <label className="text-[10px] text-gray-500 font-bold uppercase block mb-1">
                    Stroke Index Tee
                  </label>
                  <select
                    required
                    className="border p-2 rounded w-full bg-white text-sm"
                    value={p.gender || ""}
                    onChange={(e) =>
                      updatePlayerParam(p.id, "gender", e.target.value)
                    }
                  >
                    <option value="">-- Select men's or women's tees --</option>
                    <option value="Man">Men's tees</option>
                    <option value="Woman">Women's tees</option>
                  </select>
                </div>

                {activeComp.format === 'BBS' && (
                  <div>
                     <label className="text-[10px] text-gray-500 font-bold uppercase">Two-Ball Team Name</label>
                     <input 
                       type="text" 
                       placeholder="e.g. Team A" 
                       className="border p-1.5 text-xs rounded w-full font-semibold mt-1" 
                       value={p.teamName || ''} 
                       onChange={(e) => updatePlayerParam(p.id, 'teamName', e.target.value)} 
                     />
                  </div>
                )}
              </li>
            ))}
          </ul>
        </div>

        <button
          onClick={saveTeamAndStartScoring}
          className="bg-green-700 text-white p-3 rounded w-full font-bold text-lg shadow"
        >
          Save Fourball & Open Scorecard
        </button>
      </div>
    );
  }

  if (activeComp) {
    return (
      <Scorecard
        comp={activeComp}
        players={players}
        currentUserDoc={currentUserDoc}
        db={db}
        exitToMenu={() => setActiveComp(null)}
        openTeamSetup={() => setIsEditingTeam(true)}
      />
    );
  }

  return (
    <div className="space-y-6">
      <div className="bg-white p-4 rounded shadow space-y-4">
        <h2 className="text-xl font-bold">Join Competition</h2>
        <input
          type="text"
          placeholder="Competition Code"
          className="border p-2 rounded w-full uppercase text-center text-xl font-bold tracking-widest"
          value={compCode}
          onChange={(e) => setCompCode(e.target.value)}
        />
        <button
          onClick={joinCompetitionAndFetchParticipants}
          className="bg-green-700 text-white p-3 rounded w-full font-bold text-lg shadow"
        >
          Verify Code & Setup Fourball
        </button>
      </div>

      <div className="bg-white p-4 rounded shadow">
        <h2 className="text-xl font-bold mb-4 border-b pb-2">
          My Rounds & History
        </h2>
        {history.length === 0 ? (
          <p className="text-gray-500 text-sm">No recorded rounds found.</p>
        ) : (
          <div className="space-y-2">
            {history.map((comp) => (
              <div
                key={comp.id}
                className="flex justify-between items-center bg-gray-50 p-3 border rounded shadow-sm"
              >
                <div>
                  <p className="font-bold">{comp.name}</p>
                  {comp.companyName && (
                    <p className="text-xs text-gray-500 font-semibold">
                      Host: {comp.companyName}
                    </p>
                  )}
                  <p className="text-xs text-gray-500">
                    {comp.format} | Code: {comp.compCode}
                  </p>
                </div>
                <button
                  onClick={() => resumeRound(comp)}
                  className="bg-blue-600 text-white px-4 py-2 rounded font-bold text-sm"
                >
                  Resume / View
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// --- SCORECARD ---
function Scorecard({
  comp,
  players,
  currentUserDoc,
  db,
  exitToMenu,
  openTeamSetup,
}) {
  const [currentHole, setCurrentHole] = useState(1);
  const [scores, setScores] = useState({});
  const [runningTotals, setRunningTotals] = useState({});
  const [scoreDrafts, setScoreDrafts] = useState({});
  const [savingScoreKey, setSavingScoreKey] = useState(null);
  const [scoreError, setScoreError] = useState("");

  useEffect(() => {
    const q = query(collection(db, `competitions/${comp.id}/scores`));
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const loadedScores = {};
      snapshot.forEach((doc) => {
        const data = doc.data();
        loadedScores[`${data.playerId}_${data.hole}`] = data;
      });
      setScores(loadedScores);
      recalculateRunningTotals(loadedScores);
    });
    return () => unsubscribe();
  }, [comp.id, db]);

  const recalculateRunningTotals = (currentScores) => {
    const newTotals = {};
    players.forEach((p) => {
      newTotals[p.id] = {
        totalGross: 0,
        totalPoints: 0,
        totalNet: 0,
      };
    });

    Object.values(currentScores).forEach((s) => {
      if (!newTotals[s.playerId])
        newTotals[s.playerId] = {
          totalGross: 0,
          totalPoints: 0,
          totalNet: 0,
        };
      newTotals[s.playerId].totalGross += s.gross;
      newTotals[s.playerId].totalPoints += s.points;
      newTotals[s.playerId].totalNet += s.net;
    });

    setRunningTotals(newTotals);
  };

  const holeData = comp.holes
    ? comp.holes[currentHole - 1]
    : { par: 4, strokeIndex: 1 };

  const calculateScore = (gross, playerId) => {
    if (!gross || gross <= 0) return null;
    const player = players.find((p) => p.id === playerId);
    const hcp = player?.handicap || 0;
    const strokeIndex =
      player?.gender === "Woman"
        ? holeData.womensStrokeIndex ?? holeData.strokeIndex
        : holeData.mensStrokeIndex ?? holeData.strokeIndex;

    let strokesReceived = Math.floor(hcp / 18);
    if (hcp % 18 >= strokeIndex) strokesReceived += 1;

    const net = gross - strokesReceived;
    const points = Math.max(0, holeData.par - net + 2);
    return { gross: Number(gross), net, points, strokesReceived };
  };

  const saveScore = async (playerId, grossValue) => {
    const isMemberOfFourball = players.some((p) => p.id === playerId);
    if (!isMemberOfFourball) return;

    const key = `${playerId}_${currentHole}`;
    const gross = Number(grossValue);
    if (!Number.isInteger(gross) || gross <= 0) return;

    const scoreData = calculateScore(grossValue, playerId);
    if (!scoreData) return;

    const scoreRecord = {
      playerId,
      hole: currentHole,
      ...scoreData,
      timestamp: new Date(),
    };
    setScoreError("");
    setSavingScoreKey(key);
    try {
      await setDoc(doc(db, `competitions/${comp.id}/scores`, key), scoreRecord);
      const updatedScores = { ...scores, [key]: scoreRecord };
      setScores(updatedScores);
      recalculateRunningTotals(updatedScores);
      setScoreDrafts((drafts) => {
        const updatedDrafts = { ...drafts };
        delete updatedDrafts[key];
        return updatedDrafts;
      });
    } catch (error) {
      setScoreError(`Unable to save score: ${error.message}`);
    } finally {
      setSavingScoreKey(null);
    }
  };

  const deleteScore = async (playerId) => {
    const key = `${playerId}_${currentHole}`;
    if (!scores[key]) return;

    setScoreError("");
    setSavingScoreKey(key);
    try {
      await deleteDoc(doc(db, `competitions/${comp.id}/scores`, key));
      const updatedScores = { ...scores };
      delete updatedScores[key];
      setScores(updatedScores);
      recalculateRunningTotals(updatedScores);
      setScoreDrafts((drafts) => {
        const updatedDrafts = { ...drafts };
        delete updatedDrafts[key];
        return updatedDrafts;
      });
    } catch (error) {
      setScoreError(`Unable to delete score: ${error.message}`);
    } finally {
      setSavingScoreKey(null);
    }
  };

  const renderSponsorMedia = (url) => {
    if (!url) return null;
    
    // YouTube Support
    if (url.includes("youtube.com") || url.includes("youtu.be")) {
      let embedUrl = url;
      if (url.includes("watch?v="))
        embedUrl = url.replace("watch?v=", "embed/");
      else if (url.includes("youtu.be/"))
        embedUrl = url.replace("youtu.be/", "youtube.com/embed/");
      return (
        <iframe
          className="w-full aspect-video rounded mt-2 shadow-sm"
          src={embedUrl}
          title="Sponsor"
          allowFullScreen
        ></iframe>
      );
    }

    // Direct Video Support (.mp4, .mpeg, .mov, .webm)
    const lowerUrl = url.toLowerCase();
    if (lowerUrl.endsWith(".mp4") || lowerUrl.endsWith(".mpeg") || lowerUrl.endsWith(".mov") || lowerUrl.endsWith(".webm")) {
      return (
        <video
          className="w-full aspect-video rounded mt-2 object-cover bg-black"
          src={url}
          autoPlay
          muted
          loop
          playsInline
          controls
        ></video>
      );
    }

    // Standard Image Support (JPEG, PNG, WebP, etc.)
    return (
      <img
        src={url}
        alt="Sponsor"
        className="w-full h-40 object-contain bg-white rounded mt-2 shadow-sm"
      />
    );
  };

  const activeSponsor = comp.sponsors ? comp.sponsors[currentHole] : null;

  return (
    <div className="bg-white rounded shadow pb-4">
      <div className="bg-gray-800 text-white p-4 rounded-t text-center sticky top-0 z-10">
        <div className="flex justify-between items-center text-xs">
          <button onClick={exitToMenu} className="text-gray-300 font-bold">
            &larr; Dashboard
          </button>
          <button
            onClick={openTeamSetup}
            className="bg-green-700 text-white px-2.5 py-1 rounded font-bold shadow"
          >
            Edit Fourball
          </button>
        </div>

        {comp.eventLogo && (
          <div className="flex justify-center mt-3 mb-1">
            <img
              src={comp.eventLogo}
              alt="Event Logo"
              className="h-10 object-contain rounded bg-white p-1"
            />
          </div>
        )}

        <div className="truncate my-2">
          <h2 className="font-bold text-lg">{comp.name}</h2>
          {comp.companyName && (
            <p className="text-xs text-yellow-300 font-semibold uppercase">
              Host: {comp.companyName}
            </p>
          )}
          <p className="text-xs text-gray-300">
            {comp.format} {comp.scrambleScoringMode ? `(${comp.scrambleScoringMode})` : ""}
          </p>
        </div>

        <div className="flex justify-between items-center mt-2">
          <button
            onClick={() => setCurrentHole(Math.max(1, currentHole - 1))}
            className="bg-gray-600 px-4 py-2 rounded font-bold"
          >
            &larr;
          </button>
          <div>
            <span className="text-3xl font-bold block">Hole {currentHole}</span>
            <span className="text-sm">
              Par {holeData.par} | Men's SI {holeData.mensStrokeIndex ?? holeData.strokeIndex} | Women's SI {holeData.womensStrokeIndex ?? holeData.strokeIndex}
            </span>
          </div>
          <button
            onClick={() => setCurrentHole(Math.min(18, currentHole + 1))}
            className="bg-gray-600 px-4 py-2 rounded font-bold"
          >
            &rarr;
          </button>
        </div>
      </div>

      <div className="p-4 space-y-4">
        <CompetitionMessages comp={comp} db={db} />

        {activeSponsor && (
          <div className="bg-yellow-100 border-2 border-yellow-400 p-3 text-center rounded-lg shadow-inner">
            <span className="text-xs text-yellow-700 uppercase font-bold tracking-widest">
              Sponsored By
            </span>
            <p className="font-black text-xl text-yellow-900">
              {activeSponsor.name}
            </p>
            {renderSponsorMedia(activeSponsor.mediaUrl)}
          </div>
        )}

        <div className="bg-green-50 p-2 rounded border border-green-200 text-center">
          <p className="text-xs font-bold text-green-800 uppercase tracking-wide">
            Your Fourball Scorecard
          </p>
        </div>

        {scoreError && (
          <p role="alert" className="text-sm text-red-700 bg-red-50 border border-red-200 rounded p-3">
            {scoreError}
          </p>
        )}

        {players.map((p) => {
          const key = `${p.id}_${currentHole}`;
          const currentHoleScore = scores[key];
          const scoreDraft =
            scoreDrafts[key] ?? (currentHoleScore ? String(currentHoleScore.gross) : "");
          const isSaving = savingScoreKey === key;
          const isScoreActionInProgress = savingScoreKey !== null;
          const totals = runningTotals[p.id] || {
            totalGross: 0,
            totalPoints: 0,
            totalNet: 0,
          };

          return (
            <div
              key={p.id}
              className="bg-gray-50 p-3 rounded-lg border shadow-sm"
            >
              <div className="flex justify-between items-center gap-3 mb-2">
                <div>
                  <p className="font-bold text-lg">
                    {p.name} {p.id === currentUserDoc.id && "(You)"}
                  </p>
                  <p className="text-xs text-gray-500 font-bold uppercase">
                    HCP: {p.handicap} | {p.gender ? getStrokeIndexTeeLabel(p.gender) : "Stroke index tee not selected"}{" "}
                    {p.teamName && p.teamName !== "Individual"
                      ? `| Team: ${p.teamName}`
                      : ""}
                  </p>
                </div>
                <input
                  type="number"
                  min="1"
                  step="1"
                  inputMode="numeric"
                  aria-label={`${p.name}'s gross score for hole ${currentHole}`}
                  placeholder="Gross"
                  className="border-2 border-green-700 w-24 text-center text-2xl p-2 rounded-lg font-bold text-gray-800 focus:outline-none focus:ring-4 focus:ring-green-300"
                  value={scoreDraft}
                  onChange={(e) =>
                    setScoreDrafts((drafts) => ({
                      ...drafts,
                      [key]: e.target.value,
                    }))
                  }
                />
              </div>

              <div className="flex gap-2 mb-2">
                <button
                  type="button"
                  onClick={() => saveScore(p.id, scoreDraft)}
                  disabled={
                    isScoreActionInProgress ||
                    !Number.isInteger(Number(scoreDraft)) ||
                    Number(scoreDraft) <= 0 ||
                    String(currentHoleScore?.gross ?? "") === scoreDraft
                  }
                  className="flex-1 bg-green-700 hover:bg-green-800 disabled:bg-gray-300 disabled:text-gray-600 text-white py-2 rounded font-bold text-sm"
                >
                  {isSaving ? "Saving..." : "Save score"}
                </button>
                <button
                  type="button"
                  onClick={() => deleteScore(p.id)}
                  disabled={isScoreActionInProgress || !currentHoleScore}
                  className="flex-1 bg-white hover:bg-red-50 disabled:bg-gray-100 disabled:text-gray-400 text-red-700 border border-red-300 py-2 rounded font-bold text-sm"
                >
                  {isSaving ? "Saving..." : "Delete score"}
                </button>
              </div>

              <div className="grid grid-cols-3 text-xs bg-white p-2 rounded border border-gray-200 text-center font-semibold">
                <span className="text-gray-600 border-r">
                  Hole Pts:{" "}
                  <strong className="text-black text-sm block">
                    {currentHoleScore ? currentHoleScore.points : "-"}
                  </strong>
                </span>
                <span className="text-gray-600 border-r">
                  Tot Pts:{" "}
                  <strong className="text-black text-sm block">
                    {totals.totalPoints}
                  </strong>
                </span>
                <span className="text-gray-600">
                  Tot Gross:{" "}
                  <strong className="text-black text-sm block">
                    {totals.totalGross}
                  </strong>
                </span>
              </div>
            </div>
          );
        })}
      </div>
      <Leaderboard comp={comp} db={db} />
    </div>
  );
}

function CompetitionMessages({ comp, db, user, canSend = false }) {
  const [messages, setMessages] = useState([]);
  const [messageDraft, setMessageDraft] = useState("");
  const [isSending, setIsSending] = useState(false);
  const [messageError, setMessageError] = useState("");

  useEffect(() => {
    const competitionRef = doc(db, "competitions", comp.id);
    return onSnapshot(
      competitionRef,
      (snapshot) => {
        const loadedMessages = snapshot.data()?.messages;
        if (loadedMessages == null) {
          setMessages([]);
          setMessageError("");
          return;
        }
        if (!Array.isArray(loadedMessages)) {
          setMessageError("Unable to load event messages: invalid message data.");
          return;
        }
        setMessages(
          [...loadedMessages].sort(
            (a, b) =>
              new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
          )
        );
        setMessageError("");
      },
      (error) => {
        setMessageError(`Unable to load event messages: ${error.message}`);
      }
    );
  }, [comp.id, db]);

  const sendMessage = async () => {
    const message = messageDraft.trim();
    if (!message || !canSend || !user) return;

    setIsSending(true);
    setMessageError("");
    try {
      await updateDoc(doc(db, "competitions", comp.id), {
        messages: arrayUnion({
          id: doc(collection(db, `competitions/${comp.id}/messages`)).id,
          message,
          authorId: user.id,
          authorName: user.name || "Organizer",
          createdAt: new Date().toISOString(),
        }),
      });
      setMessageDraft("");
    } catch (error) {
      setMessageError(`Unable to send event message: ${error.message}`);
    } finally {
      setIsSending(false);
    }
  };

  return (
    <section className="bg-white p-4 rounded shadow border border-blue-200 space-y-3">
      <div>
        <h3 className="font-bold text-lg text-blue-900">
          {canSend ? "Player updates" : "Event updates"}
        </h3>
        <p className="text-xs text-gray-600">
          {canSend
            ? "Messages appear live in the scorecards of all players in this competition."
            : "Updates from your organizer appear here during the golf day."}
        </p>
      </div>

      {canSend && (
        <div className="space-y-2">
          <textarea
            aria-label="Message to all players"
            maxLength={500}
            rows={3}
            value={messageDraft}
            onChange={(event) => setMessageDraft(event.target.value)}
            placeholder="Share an important update with all players..."
            className="w-full border rounded p-2 text-sm resize-y"
          />
          <div className="flex justify-between items-center gap-3">
            <span className="text-xs text-gray-500">
              {messageDraft.length}/500 characters
            </span>
            <button
              type="button"
              onClick={sendMessage}
              disabled={isSending || !messageDraft.trim()}
              className="bg-blue-700 hover:bg-blue-800 disabled:bg-gray-300 text-white px-4 py-2 rounded font-bold text-sm"
            >
              {isSending ? "Sending..." : "Send to all players"}
            </button>
          </div>
        </div>
      )}

      {messageError && (
        <p role="alert" className="text-sm text-red-700 bg-red-50 border border-red-200 rounded p-2">
          {messageError}
        </p>
      )}

      {messages.length === 0 ? (
        <p className="text-sm text-gray-500">No event updates yet.</p>
      ) : (
        <ul className="space-y-2">
          {messages.map((item) => {
            const sentAt = item.createdAt ? new Date(item.createdAt) : null;
            return (
              <li
                key={item.id}
                className="bg-blue-50 border border-blue-100 rounded p-3"
              >
                <p className="text-sm text-gray-800 whitespace-pre-wrap break-words">
                  {item.message}
                </p>
                <p className="text-xs text-gray-500 mt-2">
                  {item.authorName || "Organizer"}
                  {sentAt && !Number.isNaN(sentAt.getTime())
                    ? ` · ${sentAt.toLocaleString()}`
                    : " · Just now"}
                </p>
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}

// --- REAL-TIME LEADERBOARD RENDERING ---
function Leaderboard({ comp, db, tvMode = false }) {
  const [board, setBoard] = useState([]);
  const [isTeamFormat, setIsTeamFormat] = useState(false);
  const [isPtsFormat, setIsPtsFormat] = useState(true);

  useEffect(() => {
    if (!comp.id) return;

    const q = query(collection(db, `competitions/${comp.id}/scores`));
    const unsubscribe = onSnapshot(q, async (snapshot) => {
      const sList = [];
      snapshot.forEach((doc) => sList.push(doc.data()));

      const pSnap = await getDocs(
        collection(db, `competitions/${comp.id}/participants`)
      );
      const pList = [];
      pSnap.forEach((d) => pList.push({ id: d.id, ...d.data() }));

      const data = buildLeaderboardData(comp, pList, sList);

      setBoard(data.board);
      setIsTeamFormat(data.teamFormat);
      setIsPtsFormat(data.isPointsFormat);
    });

    return () => unsubscribe();
  }, [comp.id, comp.format, db]);

  const isStableford = comp.format === "Stableford";
  const isScrambleNet = comp.format === "Scramble" && comp.scrambleScoringMode === "Net";

  const bgClass = tvMode ? "bg-gray-900 border-gray-700" : "bg-white border";
  const textClass = tvMode ? "text-gray-100" : "text-gray-800";
  const headerTextSize = tvMode ? "text-sm sm:text-xl p-4" : "text-[10px] sm:text-xs p-2";
  const rowTextSize = tvMode ? "text-lg sm:text-2xl p-4" : "text-xs sm:text-sm p-2";
  const highlightClass = tvMode ? "text-green-400 font-black" : "text-green-700 font-bold";
  const topRowClass = tvMode ? "bg-green-900 border-green-600 font-bold" : "bg-yellow-50 font-bold";

  return (
    <div className={`mt-4 ${tvMode ? "" : "p-4 border-t bg-gray-100"}`}>
      {!tvMode && (
         <h3 className="font-bold text-lg mb-3 text-green-800 text-center uppercase tracking-wider">
           Overall Competition Leaderboard
         </h3>
      )}
      
      <div className={`${bgClass} rounded-xl shadow-2xl overflow-hidden`}>
        
        <div className={`grid grid-cols-12 bg-gray-800 text-white ${headerTextSize} font-bold text-center uppercase tracking-wide border-b-2 border-gray-600`}>
          <div className="col-span-5 text-left pl-4">{isTeamFormat ? "Team" : "Player"}</div>
          <div className="col-span-2">Thru</div>
          {isStableford && (
            <>
              <div className="col-span-2">Gross</div>
              <div className="col-span-3">Pts</div>
            </>
          )}
          {isScrambleNet && (
            <>
              <div className="col-span-2">HCP</div>
              <div className="col-span-3">Net</div>
            </>
          )}
          {!isStableford && !isScrambleNet && (
             <div className="col-span-5">{isPtsFormat ? "Pts" : "Gross"}</div>
          )}
        </div>

        {board.map((item, index) => (
          <div
            key={item.id || item.name}
            className={`grid grid-cols-12 ${rowTextSize} text-center items-center border-b ${tvMode ? "border-gray-700 hover:bg-gray-800" : ""} ${textClass} ${
              index === 0 ? topRowClass : ""
            }`}
          >
            <div className="col-span-5 text-left pl-4 truncate">
              <span className={tvMode ? "text-gray-400 font-mono mr-2" : "mr-1"}>{index + 1}.</span> 
              <span className="font-bold">{item.name}</span>
              {item.members && (
                <span className={`block ${tvMode ? "text-sm text-gray-500" : "text-[10px] text-gray-500"} font-normal truncate mt-1`}>
                  ({item.members.join(", ")})
                </span>
              )}
            </div>
            <div className={tvMode ? "text-gray-400 font-mono" : "text-gray-500"}>{item.holesPlayed}</div>
            
            {isStableford && (
              <>
                <div className={`col-span-2 ${tvMode ? "text-gray-300" : "text-gray-700"}`}>{item.totalGross}</div>
                <div className={`col-span-3 ${highlightClass}`}>{item.totalPoints}</div>
              </>
            )}

            {isScrambleNet && (
              <>
                <div className={`col-span-2 ${tvMode ? "text-gray-400" : "text-gray-500"}`}>{item.teamHcp}</div>
                <div className={`col-span-3 ${tvMode ? "text-blue-400 font-black" : "font-bold text-blue-700"}`}>{item.totalNet}</div>
              </>
            )}

            {!isStableford && !isScrambleNet && (
               <div className={`col-span-5 ${highlightClass}`}>
                  {isPtsFormat ? item.totalPoints : item.totalGross}
               </div>
            )}
          </div>
        ))}
        {board.length === 0 && (
          <p className={`text-center p-8 ${tvMode ? "text-gray-500 text-xl" : "text-gray-500 text-sm"}`}>
            Waiting for first scores to be submitted...
          </p>
        )}
      </div>
    </div>
  );
}